import React, { useEffect, useRef } from 'react';
import { useInfoBanner } from 'app/components/InfoBannerProvider';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { AuthorizedAccountsApi } from 'app/modules/settings/api/authorizedAccounts';
import { queryKey } from 'constants/queryKey';
import { TextLink } from '@kontentino/ui';
import routes from 'constants/routes';
import { useUserPermissions } from 'app/hooks/useUserPermissions';
import { UserPermission } from 'constants/userPermission';
import { BrowserStorage } from 'utils/browserStorage';
import Link from 'components/shared/Link';
import { useUser } from 'modules/user/userSelector';
import { routePathToUrl } from 'utils/url';
import { useLocation } from 'react-router-dom';

/**
 * Workspace-wide warning that some profiles need reconnecting, pointing at Settings.
 *
 * The calendar shows a richer, filter-scoped banner instead
 * (`useCalendarDisconnectedProfilesBanner`), so this one stands down there — both live in
 * the single app-wide InfoBanner slot and only one can own it.
 */
export const useDisconnectedProfilesBanner = () => {
  const { t } = useTranslation();
  const { content: bannerContent, showBanner, clearBanner } = useInfoBanner();
  const hasEvaluatedRef = useRef(false);
  const ownsBannerRef = useRef(false);
  // The calendar-scoped banner clears the slot in an effect that runs after this one, so
  // the value captured in this render is stale on the commit where the user leaves the
  // calendar. Reading it in a microtask lets that clear land first.
  const bannerContentRef = useRef(bannerContent);
  useEffect(() => {
    bannerContentRef.current = bannerContent;
  }, [bannerContent]);
  const { hasSome } = useUserPermissions();
  const { isClient } = useUser();
  const { pathname } = useLocation();

  // useLocation() strips the router basename, so this compares against '/posts/calendar'
  // rather than '/app/posts/calendar'. Adding the prefix here would break the match.
  const isCalendar = pathname.startsWith(routes.POSTS_CALENDAR);

  const canAccessSocialLogin =
    !isClient && hasSome(UserPermission.ACCESS_SOCIAL_LOGIN);

  const disconnectedPagesQuery = useQuery(
    queryKey.authorizationPages('disconnected'),
    () => AuthorizedAccountsApi.getPages('disconnected'),
    {
      enabled:
        canAccessSocialLogin &&
        sessionStorage.getItem(
          BrowserStorage.keys.Banners.DisconnectedProfilesDismissed,
        ) !== 'true',
    },
  );

  const isQuerySettled =
    disconnectedPagesQuery.isSuccess && !disconnectedPagesQuery.isFetching;

  useEffect(() => {
    if (isCalendar) {
      // Re-arm so the banner is re-evaluated on the way back out of the calendar.
      hasEvaluatedRef.current = false;

      if (ownsBannerRef.current) {
        ownsBannerRef.current = false;
        clearBanner();
      }

      return;
    }

    if (
      hasEvaluatedRef.current ||
      !isQuerySettled ||
      !canAccessSocialLogin ||
      sessionStorage.getItem(
        BrowserStorage.keys.Banners.DisconnectedProfilesDismissed,
      ) === 'true'
    ) {
      return;
    }

    hasEvaluatedRef.current = true;

    if (disconnectedPagesQuery.data?.length === 0) {
      return;
    }

    const timeout = setTimeout(() => {
      if (bannerContentRef.current != null) {
        return;
      }

      ownsBannerRef.current = true;

      showBanner(
        <div className="tw-flex tw-items-center tw-gap-1 tw-text-sm">
          <span className="tw-font-semibold">{t('reconnectionRequired')}.</span>{' '}
          {t('reconnectionRequiredDescription')}{' '}
          <TextLink>
            <Link
              to={routePathToUrl({ pathname: routes.SETTINGS.AUTHORIZATION })}
              className="tw-font-semibold !tw-text-warning-140 !tw-underline hover:tw-text-warning-160"
            >
              {t('reconnectYourProfiles')}
            </Link>
          </TextLink>
        </div>,
        {
          variant: 'warning',
          onClose: () => {
            ownsBannerRef.current = false;
            sessionStorage.setItem(
              BrowserStorage.keys.Banners.DisconnectedProfilesDismissed,
              'true',
            );
          },
        },
      );
    }, 0);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isQuerySettled, isCalendar]);

  useEffect(
    () => () => {
      if (ownsBannerRef.current) {
        ownsBannerRef.current = false;
        clearBanner();
      }
    },
    [clearBanner],
  );
};
